import AddressC from '@/gf/components/Address'
import Button from '@/gf/components/ButtonOld'
import Layout from '@/gf/components/Layout'
import Modal from '@/gf/components/Modal'
import Page from '@/gf/components/Page'
import Spinner from '@/gf/components/Spinner'
import TextArea from '@/gf/components/TextArea'
import useMsgs from '@/gf/hooks/useMsgs'
import useToggle from '@/gf/hooks/useToggle'
import GraphQL from '@/gf/modules/GraphQL'
import MoneyM from '@/gf/modules/Money'
import * as api from '@/suppliers/modules/api'
import { ApolloError } from '@apollo/client'
import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  CreateOrderFormCustomer,
  CustomerCardRef,
  EditLineItem,
  EditLineItemChanges,
  Money,
  ProductSearchResult,
  ShippingCost,
} from '../../types'
import AddCustomerCard from '../components/AddCustomerCardForCreateOrder'
import AddItemsCard from '../components/AddItemsCardForCreateOrder'
import CustomerCard from '../components/CustomerCardForCreateOrder'
import Frame from '../components/Frame'
import NewQuotePayment from '../components/NewQuotePayment'
import NewStoreOrders from '../components/NewStoreOrders'
import useCreateQuote from '../hooks/useCreateQuote'
import useFetchDiscounts from '../hooks/useFetchDiscounts'
import useSession from '../hooks/useSession'
import useStores from '../hooks/useStores'
import CreateQuoteStoreForm from '../modules/CreateQuoteStoreForm'
import StoreOrderM from '../modules/StoreOrder'
import { CreateQuoteForm } from '../types'

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [
    { name: 'Orders', href: '/orders' },
    { name: 'Create Quote', href: `/orders/create` },
  ],
}

const CreateOrder = () => {
  const { store, user } = useSession()
  const navigate = useNavigate()
  const [_msgs, msgsMgr] = useMsgs()
  const [spinnerLive, spinner] = useToggle()
  const allStores = useStores().data?.allStores || []
  const [shippingCosts, setShippingCosts] = React.useState<ShippingCost[]>([])
  const [newOrderLineItems, setNewOrderLineItems] = React.useState<EditLineItem[]>([])
  const [productsFromQuery, setProductsFromQuery] = React.useState<ProductSearchResult[]>([])
  const [additions, setAdditions] = React.useState<EditLineItemChanges>({})
  const [orderCustomer, setOrderCustomer] = React.useState<CreateOrderFormCustomer>()
  const [form, setForm] = React.useState<CreateQuoteForm>({ stores: [] })

  const discounts = (() => {
    const result = useFetchDiscounts().data?.discounts

    return orderCustomer
      ? result?.filter((d) => d.organization.id === orderCustomer.account?.id)
      : result
  })()

  const customerCardRef = useRef<CustomerCardRef>(null)
  const [createModalOpen, createModalToggler] = useToggle()
  const [addressNotDeliverableModalOpen, addressNotDeliverableModalToggler] = useToggle(false)
  const [createEmailAdditionalCommentsText, setCreateEmailAdditionalCommentsText] =
    React.useState<string>('')

  const [estimatedShippingCost, setEstimatedShippingCost] = useState<Money>()
  const createQuoteMutation = useCreateQuote()

  const openShippingAddress = () => {
    customerCardRef.current?.focusShippingAddress()
  }

  // any time the additions map is updated, update the list of new line items
  React.useEffect(() => {
    const addedLineItems = Object.keys(additions)
      .filter((key) => !additions[key].id)
      .map((key) => additions[key])

    api
      .calculateShippingCost(
        store.id,
        addedLineItems.map((ali) => ({
          productId: ali.product.id,
          quantity: ali.quantity,
          inStock: ali.inStock,
          availableAt: ali.availableAt,
          unitPrice: MoneyM.toDecimal(ali.unitPrice),
        }))
      )
      .then(setEstimatedShippingCost)

    setNewOrderLineItems(addedLineItems)
  }, [additions])

  const newStoreOrders = StoreOrderM.editLineItemsToNewStoreOrders(
    newOrderLineItems,
    allStores,
    shippingCosts,
    discounts
  )

  const createQuote = (allowUnverifiedAddress = false) => {
    if (!orderCustomer) {
      createModalToggler.off()
      msgsMgr.add('A customer must be created or selected.', 'negative')
      return
    }

    if (orderCustomer && !orderCustomer.shippingAddress) {
      createModalToggler.off()
      msgsMgr.add('A customer must have a valid shipping address', 'negative')
      return
    }

    if (newStoreOrders.length === 0) {
      createModalToggler.off()
      msgsMgr.add('An order must contain items.', 'negative')
      return
    }

    spinner.on()

    // probably don't need to filter; just being cautious
    const storeIds = newStoreOrders
      .filter((nso) => nso.lineItems.length !== 0)
      .map((nso) => nso.store.id)

    const stores = form.stores
      .filter((s) => storeIds.includes(s.id))
      // if order is being shipped, clear out pickup address
      .map((s) => (s.pickup ? s : { ...s, pickupAddress: null }))

    const lineItems = newStoreOrders
      .flatMap((nso) => nso.lineItems)
      .map((li) => ({
        productId: li.product.id,
        quantity: li.quantity,
        inStock: li.inStock,
        availableAt: li.availableAt,
        unitPrice: MoneyM.toDecimal(li.unitPrice),
      }))

    const timingDetails = stores
      .map((s) => ({ storeId: s.id, timingDetails: s.timingDetails }))
      .filter((tdi) => tdi.timingDetails.length > 0)

    const customer = orderCustomer
      ? StoreOrderM.orderFormCustomerToCustomerInput(orderCustomer)
      : null

    createQuoteMutation({
      stores: stores.map((s) => ({
        id: s.id,
        pickupAddress: s.pickupAddress ? GraphQL.cleanGraphQlInput(s.pickupAddress) : null,
      })),
      notes: createEmailAdditionalCommentsText,
      lineItems,
      shippingCosts,
      timingDetails,
      pickupInputs: stores.map((s) => ({ storeId: s.id, pickup: s.pickup })),
      payOnAccount: false,
      payOnInvoice: false,
      allowUnverifiedAddress,
      requestForQuoteId: null,
      customer,
      customerShim: null,
    })
      .then(() => {
        msgsMgr.add('Order created. Email sent to customer.', 'positive')
        navigate('/orders')
      })
      .catch((err: ApolloError) => {
        const { message } = err

        spinner.off()
        createModalToggler.off()

        if (message === 'Address not deliverable') {
          Promise.resolve().then(() => {
            addressNotDeliverableModalToggler.on()
          })
        } else {
          Promise.resolve().then(() => {
            addressNotDeliverableModalToggler.off()
          })

          msgsMgr.add(message, 'negative')
        }
      })
  }

  const addProduct = (product: ProductSearchResult) => {
    const existingLineItem = newOrderLineItems.find((li) => li.product.id === product.id)
    const currencyCode = existingLineItem?.unitPrice.currency.code || 'USD'
    const quantity = existingLineItem ? existingLineItem.quantity + 1 : 1
    const discount = discounts?.find((d) => d.store.id === product.store.id)

    const discountedUnitPrice = MoneyM.fromDecimal(
      product.salePrice * (1 - (discount?.percent || 0) / 100),
      currencyCode
    )

    const newAdditions = {
      ...additions,
      [product.id]: {
        id: undefined,
        product,
        quantity,
        showMpn: true,
        externalId: null,
        taskNumber: null,
        suggestion: null,
        inStock: true,
        availableAt: null,
        unitPrice: MoneyM.fromDecimal(product.salePrice, currencyCode),
        discountedUnitPrice,
        extendedPrice: MoneyM.mult(discountedUnitPrice, quantity),
      },
    }

    setAdditions(newAdditions)

    if (!form.stores.find((s) => s.id === product.store.id)) {
      const pickupAddress = (user.role === 'admin' && store.address) || null
      const storeForm = CreateQuoteStoreForm.build({ id: product.store.id, pickupAddress })
      setForm({ ...form, stores: [...form.stores, storeForm] })
    }

    // Update the search results to show the correct items in the order
    const existingLineItems = Object.keys(newAdditions)
      .filter((key) => !newAdditions[key].id)
      .map((key) => newAdditions[key])

    const idsInOrder = existingLineItems.map((li) => li.product.id)

    const productSearchResults = productsFromQuery.map((p) => ({
      ...p,
      inOrder: idsInOrder?.includes(p.id) || false,
    }))

    setProductsFromQuery(productSearchResults)
  }

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title="Create Quote" previousPage="/orders">
        <Layout>
          {createModalOpen && (
            <Modal
              open={createModalOpen}
              onClose={createModalToggler.off}
              title="Create Quote"
              footerAction={
                <Button title="Submit" onClick={() => createQuote()} disabled={spinnerLive} />
              }
            >
              {spinnerLive ? (
                <Spinner />
              ) : (
                <>
                  <p>Creating a new order will notify the customer.</p>
                  <p className="mt-4">Add additional comments for customer (optional)</p>
                  <p>ATTENTION: The customer will see these comments.</p>
                  <TextArea
                    className="mt-2"
                    value={createEmailAdditionalCommentsText}
                    onChange={(e) => setCreateEmailAdditionalCommentsText(e.target.value)}
                  />
                </>
              )}
            </Modal>
          )}
          {addressNotDeliverableModalOpen && (
            <Modal
              open={addressNotDeliverableModalOpen}
              onClose={() => {
                addressNotDeliverableModalToggler.off()
                // Open the customer shipping address input
                openShippingAddress()
              }}
              title="Warning"
              cancelText="Change address"
              withIcon
              footerAction={
                <Button
                  title="Continue without changing"
                  onClick={() => createQuote(true)}
                  disabled={spinnerLive}
                />
              }
            >
              {spinnerLive ? (
                <Spinner />
              ) : (
                <>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500">
                      We were not able to verify the shipping address as a deliverable address:
                    </p>
                    {orderCustomer && (
                      <AddressC
                        className="mt-2 text-sm text-gray-700"
                        address={orderCustomer?.shippingAddress}
                      />
                    )}
                  </div>
                </>
              )}
            </Modal>
          )}
          <Layout.Section type="primary">
            <AddItemsCard
              allStores={allStores}
              productsFromQuery={productsFromQuery}
              setProductsFromQuery={setProductsFromQuery}
              newOrderLineItems={newOrderLineItems}
              addProduct={addProduct}
            />
            <NewStoreOrders
              newStoreOrders={newStoreOrders}
              shippingCosts={shippingCosts}
              setShippingCosts={setShippingCosts}
              setAdditions={setAdditions}
              additions={additions}
              productsFromQuery={productsFromQuery}
              setProductsFromQuery={setProductsFromQuery}
              form={form}
              setForm={setForm}
            />
            <NewQuotePayment
              newStoreOrders={newStoreOrders}
              shippingCosts={shippingCosts}
              suggestedShippingCost={estimatedShippingCost}
              form={form}
              primaryFooterAction={{
                id: 1,
                title: 'Create Quote',
                callBack: () => createModalToggler.on(),
              }}
            />
          </Layout.Section>
          <Layout.Section type="secondary">
            {orderCustomer ? (
              <CustomerCard
                ref={customerCardRef}
                orderCustomer={orderCustomer}
                setOrderCustomer={setOrderCustomer}
              />
            ) : (
              <AddCustomerCard setOrderCustomer={setOrderCustomer} />
            )}
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  )
}

export default CreateOrder
